import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  reduxForm,
  Field,
  getFormAsyncErrors,
  getFormValues,
} from 'redux-form';
import _ from 'lodash';
import {
  Input,
  Password,
  Button,
  Check,
  Message,
  NotificationBanner,
  Select,
  Option,
} from 'barnyard/components';
import { Row, Col } from 'barnyard/layouts';
import { messageIntercom, toPhoneNumber } from 'barnyard/utils';
import { urls as signinUrls } from '~/auth/signin/routes/urls';
import { WORKMATE_POSITIONS } from '~/app/common/constants/ClinicUserPermissions';
import track, { analyticsTypes } from '~/app/vet/core/analytics';
import IncentiveApplyPromotion from '~/app/vet/incentives/components/IncentiveApplyPromotion';
import { checkValidProfile } from '../actions';
import LearnRedirectNotice from '../components/LearnRedirectNotice';
import { SIGNUP_FORM } from '../constants';
import { emailMatchesLearnRedirectDomain, isValidString, normalizeStringLength } from './utils';

const classNamePrefix = 'Signup--Form';
const emailRegex = /^.+@.+\..+$/;
const max10LengthNormalizer = normalizeStringLength(10);


const trackPetParents = (value) => {
  if (value === 'Pet Parent') {
    track(analyticsTypes.PET_PARENT_ATTEMPT);
  }
};

const validate = (values) => {
  const errors = {};

  if (!isValidString(values.email)) {
    errors.email = 'Please enter an email';
  } else if (!emailRegex.test(values.email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (!isValidString(values.password)) {
    errors.password = 'Please enter a password';
  }

  if (values.password && values.password.length <= 6) {
    errors.password = 'Password must be at least 6 characters long';
  }

  if (!isValidString(values.contact_position)) {
    errors.contact_position = 'Please enter your position';
  }

  if (values.contact_position === 'Pet Parent') {
    errors.contact_position = (
      'Oops... it looks like you might be in the wrong place! If you are a pet parent looking to create an '
      + 'online pharmacy account for your veterinary clinic, you will want to head to your clinic\'s website and click '
      + '“Order Food & Meds,” to be redirected to the appropriate pharmacy website – or reach out to your veterinary '
      + 'clinic directly for further assistance!'
    );
  }

  if (!isValidString(values.first_name)) {
    errors.first_name = 'Please enter your first name';
  }

  if (!isValidString(values.last_name)) {
    errors.last_name = 'Please enter your last name';
  }

  if (!isValidString(values.clinic_name)) {
    errors.clinic_name = 'Please enter the clinic name';
  }

  if (_.size(values.phone_number) !== 10) {
    errors.phone_number = 'Please enter a valid 10 digit phone number';
  }

  if (!values.terms) {
    errors.terms = 'Please accept the terms and conditions';
  }

  return errors;
};

const asyncValidate = (values) => {
  // If the user has acknowledged the existing phone number, just stop checking
  if (values.isDisabledAsyncValidate || _.size(values.phone_number) !== 10) {
    return Promise.resolve();
  }
  // Check profile and store errors as form async errors
  return checkValidProfile(
    {
      phone_number: values.phone_number
    },
    {
      onError: (error) => {
        // Ignore 429 throttled errors
        if (error.code === 'throttled') {
          return;
        }
        throw error;
      }
    }
  );
};

const openTerms = (e) => {
  e.stopPropagation();
  window.open('/terms/', '__blank');
};

const UserInformationForm = ({
  handleSubmit, change, clearAsyncError, error, disabled, formValues, aErrors
}) => (
  <>
    <form className={classNamePrefix} onSubmit={handleSubmit} autoComplete="off">
      {error && (
        <Message
          color='danger'
          border={true}
          message={(
            <React.Fragment>
              <strong><h4>{error.title}</h4></strong>
              <p>{error.details}</p>
            </React.Fragment>
          )}
        />
      )}
      <Row gutter='md' className={`${classNamePrefix}__row`}>
        <Col xs={12} md={6}>
          <Field
            name='email'
            label='Enter your email*'
            placeholder='you@example.com'
            component={Input}
            autoFocus={true}
            tip='This will be your Vetcove username'
          />
        </Col>
        <Col xs={12} md={6}>
          <Field
            name='password'
            label='Create a password*'
            placeholder='Password'
            component={Password}
            meter={true}
            required={true}
          />
        </Col>
      </Row>
      {emailMatchesLearnRedirectDomain(_.get(formValues, 'email')) && (
        <LearnRedirectNotice />
      )}
      <Row gutter='md' className={`${classNamePrefix}__row u-marginTop0`}>
        <Col xs={12} md={6}>
          <Field
            name='contact_position'
            label='Your Position*'
            placeholder='Position'
            component={Select}
            onChange={trackPetParents}
          >
            {
              _.map(WORKMATE_POSITIONS, position => (
                <Option key={position} value={position} >{position}</Option>
              ))
            }
          </Field>
        </Col>
        <Col xs={12} md={6}>
          <Row gutter='sm'>
            <Col xs={12} md={6}>
              <Field
                name='first_name'
                label='First Name*'
                placeholder='First Name'
                component={Input}
              />
            </Col>
            <Col xs={12} md={6}>
              <Field
                name='last_name'
                label='Last Name*'
                placeholder='Last name'
                component={Input}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row gutter='md' className={`${classNamePrefix}__row`}>
        <Col xs={12} md={6}>
          <Field
            name='clinic_name'
            label='Clinic Name*'
            placeholder='The Purrfect Clinic'
            component={Input}
          />
        </Col>
        <Col xs={12} md={6}>
          <Field
            name='phone_number'
            label='Clinic Phone Number*'
            preIcon={['fal', 'phone']}
            placeholder='(212) 867-5309'
            displayFormat='phone'
            component={Input}
            normalize={max10LengthNormalizer}
          />
        </Col>
      </Row>
      <Row gutter='md' align='bottom' className={`${classNamePrefix}__terms`}>
        <Col>
          <Field
            name='promo_code'
            label='Promo Code (Optional)'
            placeholder='Promo Code (Optional)'
            component={IncentiveApplyPromotion}
            notFoundContent="Sorry, this promotion is not valid"
            required={false}
          />
        </Col>
        <Col>
          <Field
            name='terms'
            label={(
              <span>By signing up, I agree to the <a onClick={openTerms}>Terms of Service</a>*</span>
            )}
            component={Check}
            required={true}
          />
        </Col>
      </Row>
      <Row className={`${classNamePrefix}__buttons`} gutter='md' justify='edges'>
        <Col xs={12} md={6}>
          <h4>Already have an account? <a href={signinUrls.getLoginPage()}>Sign in</a></h4>
          <h4
            className='u-marginTop10'
          >
            Signing up as a merchant? <a href='/accounts/merchants/signup/'>Sign up here</a>
          </h4>
        </Col>
        <Col xs={12} md={6}>
          <Button
            color='primary'
            type="submit"
            full={true}
            disabled={disabled}
          >
            Create Account
          </Button>
        </Col>
      </Row>
    </form>
    {!_.isNil(_.get(aErrors, 'phone_number')) && (
      <NotificationBanner
        className='barnyard-NotifyBanner'
        style={{ bottom: 0, left: 0 }}
        type='danger'
        message={(
          <div className={`${classNamePrefix}__alertContainer`}>
            <p>
              Heads up! It looks like the phone number you&apos;ve entered is already associated with an{' '}
              existing Vetcove account.
            </p>
            <Row justify='center' gutter='sm'>
              <Col className='leftButton' span={{ xs: 12, sm: 6 }}>
                <Button
                  color='naked'
                  size={'xs'}
                  className={`${classNamePrefix}__alertContainer-button`}
                  onClick={(e) => {
                    e.preventDefault();
                    change('isDisabledAsyncValidate', true);
                    clearAsyncError('phone_number');
                  }}
                >
                  Create Duplicate Account
                </Button>
              </Col>
              <Col className='rightButton' span={{ xs: 12, sm: 6 }}>
                <Button
                  size={'xs'}
                  onClick={(e) => {
                    e.preventDefault();
                    messageIntercom(
                      'I am looking to find my existing account with phone number '
                      + `${toPhoneNumber({ number: formValues.phone_number })}`
                    );
                  }}
                >
                  Find Existing Account
                </Button>
              </Col>
            </Row>
          </div>
        )}
      />
    )}
  </>
);

UserInformationForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  clearAsyncError: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.any,
  formValues: PropTypes.object,
  aErrors: PropTypes.object,
};

const mapStateToProps = state => ({
  formValues: getFormValues(SIGNUP_FORM)(state),
  aErrors: getFormAsyncErrors(SIGNUP_FORM)(state),
});

export default connect(mapStateToProps)(reduxForm({
  form: SIGNUP_FORM,
  validate,
  asyncValidate,
  asyncBlurFields: ['phone_number']
})(UserInformationForm));
